import { useState } from "react";
import { UserPlus, ShieldCheck, ShieldOff, Save, X } from "lucide-react";
import { api } from "../../lib/api";
import { useAuth } from "../../auth/AuthContext";

const ROLE_OPTS = ["consultant", "admin"];

const emptyForm = {
  email: "",
  fullName: "",
  password: "",
  role: "consultant",
};

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  try {
    return new Date(dateStr).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "—";
  }
};

const TeamManager = ({ users, refresh }) => {
  const { user: currentUser } = useAuth();
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) {
      setError("Password must be at least 8 characters.");
      return;
    }
    setSaving(true);
    try {
      const { data: newUser } = await api.post("/auth/users", {
        email: form.email.trim().toLowerCase(),
        password: form.password,
        fullName: form.fullName.trim() || null,
      });
      // Promote to admin if selected (endpoint creates as consultant by default)
      if (form.role === "admin") {
        await api.put(`/auth/users/${newUser.id}/role`, { role: "admin" });
      }
      setForm(emptyForm);
      setShowForm(false);
      await refresh();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to create user");
    } finally {
      setSaving(false);
    }
  };

  const changeRole = async (u, role) => {
    if (role === u.role) return;
    setBusyId(u.id);
    try {
      await api.put(`/auth/users/${u.id}/role`, { role });
      await refresh();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to change role");
    } finally {
      setBusyId(null);
    }
  };

  const toggleActive = async (u) => {
    setBusyId(u.id);
    try {
      await api.patch(`/auth/users/${u.id}/active`, { isActive: !u.isActive });
      await refresh();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to update status");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="bg-white">
      <div className="flex justify-between items-center p-4 border-b border-slate-100">
        <h2 className="font-serif text-lg font-bold text-slate-900">Team Management</h2>
        <button
          onClick={() => {
            setShowForm(true);
            setForm(emptyForm);
            setError("");
          }}
          data-testid="team-add-btn"
          className="bg-[#0B2343] text-white px-4 py-2 text-sm font-medium inline-flex items-center gap-2 hover:bg-slate-800 transition-colors"
        >
          <UserPlus size={16} /> Add Agent
        </button>
      </div>

      {users.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-slate-500">No agent accounts yet</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="team-users-table">
            <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Name / Email</th>
                <th className="text-left px-4 py-3">Role</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Created</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((u) => {
                const isSelf = currentUser?.id === u.id;
                return (
                  <tr
                    key={u.id}
                    className={u.isActive ? "" : "bg-slate-50/60"}
                    data-testid={`team-user-row-${u.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="font-medium text-slate-900">
                        {u.fullName || "—"}
                        {isSelf && (
                          <span className="ml-2 text-[10px] uppercase tracking-wider text-[#C89B3C]">
                            You
                          </span>
                        )}
                      </div>
                      <div className="text-slate-500 text-xs">{u.email}</div>
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={u.role}
                        onChange={(e) => changeRole(u, e.target.value)}
                        disabled={busyId === u.id || isSelf}
                        data-testid={`team-role-select-${u.id}`}
                        className="border border-slate-200 px-2 py-1 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {ROLE_OPTS.map((r) => (
                          <option key={r} value={r}>
                            {r.charAt(0).toUpperCase() + r.slice(1)}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      {u.isActive ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 text-xs font-medium bg-emerald-50 px-2 py-1">
                          <ShieldCheck size={12} /> Active
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-slate-500 text-xs font-medium bg-slate-100 px-2 py-1">
                          <ShieldOff size={12} /> Disabled
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-slate-500 text-xs">
                      {formatDate(u.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => toggleActive(u)}
                        disabled={busyId === u.id || isSelf}
                        data-testid={`team-toggle-active-${u.id}`}
                        className={`text-xs font-medium px-3 py-1 border transition-colors ${
                          u.isActive
                            ? "border-red-200 text-red-700 hover:bg-red-50"
                            : "border-emerald-200 text-emerald-700 hover:bg-emerald-50"
                        } disabled:opacity-40 disabled:cursor-not-allowed`}
                        title={isSelf ? "You cannot change your own status" : ""}
                      >
                        {u.isActive ? "Deactivate" : "Reactivate"}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          onClick={() => setShowForm(false)}
        >
          <div
            className="bg-white w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-4 bg-[#0B2343] text-white">
              <h3 className="font-serif font-bold">New Agent Account</h3>
              <button
                onClick={() => setShowForm(false)}
                data-testid="team-form-close"
                className="text-slate-300 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                  Full Name
                </label>
                <input
                  type="text"
                  value={form.fullName}
                  onChange={(e) => setForm({ ...form, fullName: e.target.value })}
                  data-testid="team-form-fullname"
                  className="w-full border border-slate-200 px-3 py-2 focus:outline-none focus:border-[#C89B3C]"
                  placeholder="Jane Smith"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  data-testid="team-form-email"
                  className="w-full border border-slate-200 px-3 py-2 focus:outline-none focus:border-[#C89B3C]"
                  placeholder="jane@noblestrategiesconsulting.com"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                  Temporary Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  required
                  minLength={8}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  data-testid="team-form-password"
                  className="w-full border border-slate-200 px-3 py-2 focus:outline-none focus:border-[#C89B3C] font-mono text-sm"
                  placeholder="At least 8 characters"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Share this with the agent. They can reset it later.
                </p>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-500 uppercase tracking-wider mb-1">
                  Role
                </label>
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  data-testid="team-form-role"
                  className="w-full border border-slate-200 px-3 py-2 focus:outline-none focus:border-[#C89B3C]"
                >
                  <option value="consultant">Consultant (view leads, manage CMS)</option>
                  <option value="admin">Admin (full access)</option>
                </select>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3">
                  {error}
                </div>
              )}

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 text-slate-600 hover:text-slate-900 text-sm"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="team-form-submit"
                  className="bg-[#C89B3C] text-white px-4 py-2 text-sm font-medium inline-flex items-center gap-2 hover:bg-[#a8852f] transition-colors disabled:opacity-50"
                >
                  <Save size={16} />
                  {saving ? "Creating..." : "Create Account"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamManager;
